import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'

function getPathname(): string {
  return window.location.pathname
}

interface NavigateOptions {
  /** Records the screen being left on the new history entry, so the destination can offer a way back to it. */
  rememberOrigin?: boolean
}

export function navigate(path: string, { rememberOrigin = false }: NavigateOptions = {}): void {
  if (window.location.pathname !== path) {
    window.history.pushState(rememberOrigin ? { originPath: window.location.pathname } : {}, '', path)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
}

/** One query parameter of the current URL, or null; for a link that opens a screen on a given tab (`/tools?tab=census`). */
export function searchParam(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name)
}

/** The screen the current history entry was opened from, when it was opened with `rememberOrigin`. */
export function getOriginPath(): string | null {
  const state: unknown = window.history.state
  if (typeof state !== 'object' || state === null) {
    return null
  }
  const { originPath } = state as { originPath?: unknown }
  return typeof originPath === 'string' ? originPath : null
}

export function usePathname(): string {
  const [pathname, setPathname] = useState(getPathname)

  useEffect(() => {
    function handleChange(): void {
      setPathname(getPathname())
    }
    window.addEventListener('popstate', handleChange)
    return () => window.removeEventListener('popstate', handleChange)
  }, [])

  return pathname
}

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string
  children: ReactNode
  /** Records the screen being left as this one's origin, so the admin shell keeps it and offers a Back button to it. */
  rememberOrigin?: boolean
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

export function Link({ to, children, onClick, rememberOrigin = false, ...rest }: LinkProps) {
  return (
    <a
      href={to}
      onClick={(event) => {
        onClick?.(event)
        if (!event.defaultPrevented && isPlainLeftClick(event)) {
          event.preventDefault()
          navigate(to, { rememberOrigin })
        }
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
